import { combineReducers } from '@fullstory/redux-util/utils';
import { UnnamedSegmentActions } from './actions';
import { UnnamedSegmentRecord, UnnamedSegmentState, UnnamedSegmentTypes } from './types';
import { hashForUnnamedSegment } from './util';
import { noneDisabled } from '@fullstory/search-shared';

type UnnamedSegmentsById = { [id: string]: UnnamedSegmentRecord };

function unnamedSegmentsById(
  state: UnnamedSegmentsById = {},
  action: UnnamedSegmentActions,
): UnnamedSegmentsById {
  switch (action.type) {
    case UnnamedSegmentTypes.GetUnnamedSegmentRequest: {
      const unnamedSegmentId = action.body!.id!;
      return {
        ...state,
        [unnamedSegmentId]: {
          id: unnamedSegmentId,
          segmentDefinition: state[unnamedSegmentId]?.segmentDefinition || null,
          disabled: state[unnamedSegmentId]?.disabled || noneDisabled(),
          state: UnnamedSegmentState.Pending,
          name: state[unnamedSegmentId]?.name || '',
          description: state[unnamedSegmentId]?.description || '',
        },
      };
    }
    case UnnamedSegmentTypes.GetUnnamedSegmentSuccess: {
      const unnamedSegment = action.data.unnamedSegment!;
      return {
        ...state,
        [unnamedSegment.id!]: {
          id: unnamedSegment.id!,
          segmentDefinition: unnamedSegment.definition!,
          disabled: unnamedSegment.disabled || noneDisabled(),
          state: UnnamedSegmentState.Persisted,
          name: unnamedSegment.name || '',
          description: unnamedSegment.description || '',
        },
      };
    }
    case UnnamedSegmentTypes.GetUnnamedSegmentFailure: {
      const unnamedSegmentId = action.request.body!.id!;
      return {
        ...state,
        [unnamedSegmentId]: {
          id: unnamedSegmentId,
          segmentDefinition: state[unnamedSegmentId]?.segmentDefinition || null,
          disabled: state[unnamedSegmentId]?.disabled || noneDisabled(),
          state: UnnamedSegmentState.NotPersisted,
          name: state[unnamedSegmentId]?.name || '',
          description: state[unnamedSegmentId]?.description || '',
        },
      };
    }
    // Unnamed segment ids are generated up front and often used before the save network request comes back.
    // Given that, we need to store the unnamed segment on request instead of on success response.
    case UnnamedSegmentTypes.SaveUnnamedSegmentRequest: {
      const unnamedSegment = action.body!.unnamedSegment!;
      return {
        ...state,
        [unnamedSegment.id!]: {
          id: unnamedSegment.id!,
          segmentDefinition: unnamedSegment.definition!,
          disabled: unnamedSegment.disabled || noneDisabled(),
          state: UnnamedSegmentState.Pending,
          name: unnamedSegment.name || '',
          description: unnamedSegment.description || '',
        },
      };
    }
    case UnnamedSegmentTypes.SaveUnnamedSegmentSuccess: {
      const unnamedSegment = action.request.body!.unnamedSegment!;
      return {
        ...state,
        [unnamedSegment.id!]: {
          id: unnamedSegment.id!,
          segmentDefinition: unnamedSegment.definition!,
          disabled: unnamedSegment.disabled || noneDisabled(),
          state: UnnamedSegmentState.Persisted,
          name: unnamedSegment.name || '',
          description: unnamedSegment.description || '',
        },
      };
    }
    case UnnamedSegmentTypes.SaveUnnamedSegmentFailure: {
      const unnamedSegment = action.request.body!.unnamedSegment!;
      return {
        ...state,
        [unnamedSegment.id!]: {
          id: unnamedSegment.id!,
          segmentDefinition: unnamedSegment.definition!,
          disabled: unnamedSegment.disabled || noneDisabled(),
          state: UnnamedSegmentState.NotPersisted,
          name: unnamedSegment.name || '',
          description: unnamedSegment.description || '',
        },
      };
    }
  }
  return state;
}

type UnnamedSegmentIdsByHash = { [hash: number]: string };

function unnamedSegmentIdsByHash(
  state: UnnamedSegmentIdsByHash = {},
  action: UnnamedSegmentActions,
): UnnamedSegmentIdsByHash {
  switch (action.type) {
    case UnnamedSegmentTypes.GetUnnamedSegmentSuccess: {
      const unnamedSegment = action.data.unnamedSegment!;
      const hash = hashForUnnamedSegment(
        unnamedSegment.definition!,
        unnamedSegment.disabled || noneDisabled(),
        unnamedSegment.name,
        unnamedSegment.description,
      );
      return {
        ...state,
        [hash]: unnamedSegment.id!,
      };
    }
    case UnnamedSegmentTypes.SaveUnnamedSegmentRequest: {
      const unnamedSegment = action.body!.unnamedSegment!;
      const hash = hashForUnnamedSegment(
        unnamedSegment.definition!,
        unnamedSegment.disabled || noneDisabled(),
        unnamedSegment.name,
        unnamedSegment.description,
      );
      return {
        ...state,
        [hash]: unnamedSegment.id!,
      };
    }
  }
  return state;
}

type UnnamedSegmentReducerState = {
  unnamedSegmentsById: ReturnType<typeof unnamedSegmentsById>;
  unnamedSegmentIdsByHash: ReturnType<typeof unnamedSegmentIdsByHash>;
};

export const unnamedSegmentReducer = combineReducers<UnnamedSegmentReducerState>({
  unnamedSegmentsById,
  unnamedSegmentIdsByHash,
});
